<template>
  <!--  The tabindex is set to -1 to allow programmatic focus via .focus() to support 'Skip to main content',
        but should not be 0 as it disrupts DOM hierarchy and breaks tab navigation (see SLSSIT-21049).
        For accessibility on search bar, use the placeholder prop (instead of set tabindex to 0 + use aria label). -->
  <div
    ref="searchBar"
    role="search"
    class="search-bar-wrapper"
    :class="rootClasses"
    :tabindex="-1"
    @keyup.enter="handleToggleActiveThroughKeyboard"
    @focus="focusSearchInput"
  >
    <dl class="field-set type-multi-select keyword">
      <dt class="field-label">{{ t("search_keyword_label") }}</dt>
      <dd class="field-input">
        <div class="search-bar">
          <cv-search
            v-if="dataActive"
            v-model="searchKeyword"
            :size="size"
            :placeholder="placeholder"
            :disabled="disabled"
            :clear-aria-label="clearAriaLabel"
            @keyup.enter="handleKeyUpEnter"
            @focusout="handleFocusOut"
            @input="handleInput"
            @blur="handleBlur"
          />
          <svg-icon
            v-else
            :name="toggleIcon"
            class="search-toggle"
            kind="action"
            @click="handleToggleActive"
          />

          <!-- Customized close button for App header to collapse the search bar in addition to
              clearing the input as the close button for cv-search does not have this functionality -->
          <button
            v-if="(inAppHeader || autoCollapse) && dataActive"
            type="button"
            class="button close"
            @click="handleClose"
          >
            <span class="icon">
              <svg-icon name="Cross16" kind="action" />
            </span>
          </button>

          <div v-if="hasSettings" class="settings-container" @click="handleSettingsClick">
            <!-- Examples of components using the settings slot:
                - AddRecipientsContentSubpage
                - StudentSelectionListingFromGroupContentSubpage
            -->
            <slot name="settings" />
          </div>

          <slot name="filter" :filter-changed="handleFilterChanged" />
        </div>
      </dd>
    </dl>
    <slot name="guided-search" />
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, useSlots, useTemplateRef, watch } from "vue";
import DOMUtils from "@utils/dom.utils.ts";
import { isEmpty } from "@utils/var.check";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import { useI18n } from "vue-i18n";

type FilterType = "" | "dropdown" | "selection";

const props = withDefaults(
  defineProps<{
    keyword?: string;
    size?: string;
    placeholder?: string;
    disabled?: boolean;
    clearAriaLabel?: string;
    active?: boolean;
    filter?: Record<string, unknown>;
    filterType?: FilterType;
    inAppHeader?: boolean;
    autoCollapse?: boolean;
    kind?: string;
    enableFocusOut?: boolean;
    allowEmptySearch?: boolean;
    ariaLabel?: string;
  }>(),
  {
    keyword: "",
    size: "xl",
    placeholder: "Search Placeholder",
    disabled: false,
    clearAriaLabel: "Clear Search",
    active: false,
    filter: () => ({}),
    filterType: "",
    inAppHeader: false,
    autoCollapse: false,
    kind: "grey",
    enableFocusOut: false,
    allowEmptySearch: true,
    ariaLabel: undefined,
  },
);

const emit = defineEmits<{
  (e: "searchToggle"): void;
  (e: "search", payload: { keyword: string; filter: Record<string, unknown> }): void;
  (e: "clearedInput"): void;
  (e: "input", payload: { keyword: string }): void;
  (e: "settingsClick"): void;
}>();

const { t } = useI18n();
const slots = useSlots();
const searchBarRef = useTemplateRef<HTMLElement>("searchBar");

const isKeyUpEnterTriggered = ref(false);
const dataActive = ref<boolean>(props.active);
const searchKeyword = ref<string>(props.keyword || "");
const searchFilter = ref<Record<string, unknown>>(props.filter);
const searchCharacterLimit = 500;

const hasFilter = computed(() => !!slots.filter);
const hasSettings = computed(() => !!slots.settings);

const rootClasses = computed(() => {
  const filterClass = hasFilter.value ? `filter-type-${props.filterType}` : "";
  return [
    `search-bar-${props.kind}`,
    {
      _active: dataActive.value,
      "in-app-header": props.inAppHeader,
      "auto-collapse": props.autoCollapse,
    },
    filterClass,
  ];
});

const toggleIcon = computed(() => (props.inAppHeader ? "Search32" : "Search24"));

watch(
  () => props.keyword,
  (value) => {
    if (searchKeyword.value === value) return;
    searchKeyword.value = value ?? "";
  },
);

watch(
  () => props.filter,
  (value) => {
    searchFilter.value = value;
  },
);

watch(dataActive, () => {
  emit("searchToggle");
});

function focusSearchInput() {
  const host = searchBarRef.value as unknown as Element | undefined;
  if (!host) return;
  const inputs = DOMUtils.elOf(host, ".bx--search-input");
  const searchInput = inputs && inputs[0];
  if (searchInput && (searchInput as HTMLInputElement).focus) {
    (searchInput as HTMLInputElement).focus();
  }
}

function handleBlur() {
  if (props.autoCollapse && searchKeyword.value.trim() === "") {
    dataActive.value = false;
  }
}

function handleToggleActiveThroughKeyboard(evt: KeyboardEvent) {
  evt.preventDefault();
  dataActive.value = !dataActive.value;
  nextTick(() => {
    if (dataActive.value) {
      focusSearchInput();
    }
  });
}

function handleToggleActive() {
  dataActive.value = !dataActive.value;
  nextTick(() => {
    if (dataActive.value) {
      focusSearchInput();
    }
  });
}

function handleClose() {
  searchKeyword.value = "";
  dataActive.value = false;
}

function handleFilterChanged(value: Record<string, unknown>) {
  searchFilter.value = value;
  handleSearch();
}

function handleKeyUpEnter(event: KeyboardEvent) {
  event.stopPropagation();
  isKeyUpEnterTriggered.value = true;
  handleSearch();
}

function handleFocusOut() {
  if (isKeyUpEnterTriggered.value) {
    isKeyUpEnterTriggered.value = false;
    return;
  }
  if (props.enableFocusOut) {
    setTimeout(() => {
      handleSearch();
    }, 300);
  }
}

function handleSearch() {
  if (!props.allowEmptySearch && isEmpty(searchKeyword.value) && isEmpty(searchFilter.value)) {
    return;
  }
  emit("search", {
    keyword: searchKeyword.value,
    filter: searchFilter.value,
  });
}

function handleInput(value: string) {
  const hadInputBefore = !!(searchKeyword.value && searchKeyword.value.length);
  const cleared = hadInputBefore && value.length === 0;

  if (value.length > searchCharacterLimit) {
    const truncated = value.substring(0, searchCharacterLimit);
    searchKeyword.value = "";
    nextTick(() => {
      searchKeyword.value = truncated;
    });
    return;
  } else {
    searchKeyword.value = value;
  }

  if (cleared && isEmpty(searchKeyword.value)) {
    emit("clearedInput");
  }
  emit("input", { keyword: searchKeyword.value });
}

function handleSettingsClick() {
  emit("settingsClick");
}
</script>

<style lang="scss">
@import "@styles/layout/breakpoint";
@import "@styles/layout/z-map";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

.search-bar-wrapper {
  display: flex;
  position: relative;
  align-items: center;

  &.search-bar-white {
    .bx--search-input {
      background-color: $ui-01;

      &:hover {
        background-color: $hover-ui;
      }

      &:focus {
        background-color: $ui-01;
      }
    }
  }

  .keyword {
    width: 100%;
    .field-label {
      display: none;
    }
  }

  &.search-bar-grey {
    .bx--search-input {
      background-color: $ui-background;

      &:hover {
        background-color: $hover-ui;
      }

      &:focus {
        background-color: $ui-background;
      }
    }
  }

  &.auto-collapse {
    &.filter-type-dropdown {
      .button.close {
        height: 2.5rem;
      }
    }
    &.filter-type-selection {
      // Use direct child selector to prevent style spill affecting close buttons in content-popup
      .search-bar {
        > .close {
          &:hover {
            border-radius: 0;
          }
        }
      }
    }

    .bx--search-close {
      display: none;
    }
    .search-bar {
      // Use direct child selector to prevent style spill affecting close buttons in content-popup
      > .close {
        position: relative;
        right: 0.5rem;
        display: flex;
        align-items: center;
        margin-left: (-$spacing-09);
        justify-content: center;
        height: 3rem;
        min-width: 3rem;
        width: 3rem;
        cursor: pointer;
        z-index: z-map("default");

        &:hover {
          background-color: $hover-ui;
          border-radius: 0 10px 10px 0;
        }

        .icon {
          display: inline-flex;
          justify-content: center;
          align-items: center;
          width: 16px;
          height: 16px;
          background-color: $icon-02;
          border-radius: 100%;
        }

        svg {
          transform: scale(0.75);
          path {
            stroke: $ui-01;
          }
        }
      }
    }
  }
  &.in-app-header {
    position: relative;
    justify-content: center;
    margin: 0 $spacing-03;

    @supports selector(:focus-visible) {
      &:focus-visible {
        outline-offset: -3px;
        width: 48px;
        margin: 0;
      }
    }

    @include media("tablet-min") {
      max-width: 300px;
    }

    &._error {
      justify-content: flex-start;
    }

    .close {
      right: 0 !important;
      height: 2rem !important;
    }

    .bx--search-input {
      height: 2rem;
    }

    .search-bar {
      justify-content: center;
    }
  }

  &.filter-type-selection {
    .search-bar {
      .bx--search-input {
        border-radius: 10px 0 0 10px;
      }
      .filter-selection {
        border-radius: 0 10px 10px 0;
      }
      .bx--search-close {
        border-radius: 0;
      }
    }
  }

  &.error {
    .bx--search-input {
      border-color: $support-01;
    }
  }
}

.search-bar {
  display: flex;
  align-items: center;
  border-radius: 10px;
  width: 100%;

  .bx--search-close {
    border-radius: 0 10px 10px 0;
    &:focus-within {
      outline: 3px solid $inverse-support-04 !important;
      border-radius: unset;
      z-index: z-map("default");
    }

    &:before {
      display: none;
    }
    &:hover {
      background-color: $hover-ui;
    }
    &:active {
      background-color: $active-ui;
    }
    &:focus {
      outline: transparent;
      border-color: $focus;
      background-color: inherit;
    }
    svg {
      background-color: $icon-02;
      border-radius: 100%;
      path {
        fill: $ui-01;
        stroke: $ui-01;
      }
    }
  }

  .bx--search-input {
    @include carbon--type-style("body-short-01", true);
    height: 2.5rem;
    border-radius: 10px;
    border: 1px solid $ui-03;
    &:focus {
      outline: transparent;
      border-color: $focus;
    }
    &::placeholder {
      color: $text-03;
    }

    .page-browse-lesson &,
    .page-moe-library &,
    .page-basic-search-single-resource-type &,
    .page-browse-single-resource-type &,
    .page-resource-search-multi & {
      @supports selector(:focus-visible) {
        &:focus-visible {
          outline: 3px solid $inverse-support-04;
          border-radius: unset;
          z-index: auto;
          outline-offset: -1px;
        }
      }
    }
  }

  .bx--search--xl {
    .bx--search-magnifier-icon {
      height: 1.25rem;
      width: 1.25rem;
      left: 1.5rem;
      transform: translate(-50%, -50%);
    }
  }

  .cv-search {
    flex: 1 1 100%;
    border-radius: 10px;

    button.bx--search-close {
      &:hover,
      &:active {
        outline: transparent;
      }
    }
  }

  .filter-selection {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 3rem;
    flex: 0 0 3rem;
    background: $ui-01;
    cursor: pointer;
  }

  .search-toggle {
    justify-self: end;
    margin-right: $spacing-03;
    cursor: pointer;
  }

  .settings-container {
    display: flex;
    height: 3rem;
    width: 3rem;
    min-width: 3rem;
    align-items: center;
    justify-content: center;
    border-radius: 100%;
    cursor: pointer;

    > button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      border-radius: 100%;
      &:hover {
        background-color: $hover-ui;
      }
    }
  }
}
</style>
